<!--
	Copyright 2023 HALBY
	See LICENSE file for details.
 -->

<script lang="ts">
	import Carousel from '$lib/components/Carousel.svelte';
	import CarouselPagination from '$lib/components/CarouselPagination.svelte';
	import CommonPageLayout from '$lib/components/CommonPageLayout.svelte';
	import { writable } from 'svelte/store';
	import type { PageData } from './$types';
	import { assets, base } from '$app/paths';
	import Button from '$lib/components/Button.svelte';
	import ArtistJobProfile from '$lib/components/artists/ArtistJobProfile.svelte';
	import ArtistMainProfile from '$lib/components/artists/ArtistMainProfile.svelte';
	import Image from '$lib/components/Image.svelte';
	import { getLang, getLangUrl, t } from '$lib/translations/translations';
	import { page } from '$app/stores';

	export let data: PageData;
	const carouselNavIndex = writable(0);
	let carouselIndex = 0;

	let jobColor: string;
	$: {
		switch (data.artist?.meta.job_type) {
			case 'artist':
				jobColor = '#EF9FB9';
				break;
			case 'writer':
				jobColor = '#B9EAAB';
				break;
			case 'other':
				jobColor = '#A7E0F5';
				break;
		}
	}

	let calendars: { year: string; months: string[] }[] = [];
	$: {
		if (data.artist?.schedule !== undefined) {
			calendars = [];
			Object.entries(data.artist?.schedule).forEach(([year, months]) => {
				calendars.push({ year, months });
			});
		}
	}

	let lang: 'ja' | 'zh_cn' | 'zh_tw';
	$: lang = (getLang($page.url).value?.replace('-', '_') || 'ja') as 'ja' | 'zh_cn' | 'zh_tw';
</script>

<svelte:head>
	<title>{data.artist?.name[lang]}</title>
	<meta property="og:title" content={data.artist?.name[lang]} />
</svelte:head>

<CommonPageLayout>
	<main>
		<section class="top-section">
			{#if data.artist}
				<div class="mobile">
					<ArtistMainProfile artistInfo={data.artist} />
				</div>
			{/if}
			<div class="carousel-wrapper">
				<Image src="{assets}/img/pages/artists/carousel_bg_artist" alt="" />
				<div class="carousel-main-wrapper">
					<Carousel {carouselNavIndex} {carouselIndex}>
						{#each data.carousels as img}
							<Image
								class="carousel-bg-image"
								src="{assets}/img/artist/achievement/{data.artist?.meta.link}/{img}"
								alt={img}
							/>
						{/each}
					</Carousel>
				</div>
				<CarouselPagination
					carouselLength={data.carousels?.length}
					{carouselNavIndex}
					on:updateIndex={(e) => (carouselIndex = e.detail)}
				/>
			</div>
			<div class="profile-wrapper">
				{#if data.artist}
					<div class="desktop">
						<ArtistMainProfile artistInfo={data.artist} />
					</div>
					<ArtistJobProfile artistInfo={data.artist} />
				{/if}
			</div>
		</section>
		<div class="description-wrapper">
			<p class="font-base-jp">{data.artist?.profile[lang]}</p>
		</div>
		<h2 class="inner-header font-base-jp font-bold">{$t('artist.schedule.schedule')}</h2>
		<section class="schedule-section">
			<div class="calendar-wrapper">
				{#each calendars as calendar}
					<div class="calendar-per-year">
						<p>{calendar.year}</p>
						<table class="border-separate border-[0px] border-border-grey border-spacing-[2px]">
							<tr class="font-base-jp">
								{#each calendar.months as month, index}
									<td class="month "><span class="block translate-y-[1px]">{index + 1}</span></td>
								{/each}
							</tr>
							<tr class="font-base-jp">
								{#each calendar.months as status}
									<td class="status" class:status-bad={status === '×'}><span class="block translate-y-[1px]">{status}</span></td>
								{/each}
							</tr>
						</table>
					</div>
				{/each}
			</div>
			<div class="status-description font-base-jp">
				<p>◎＝{$t('artist.schedule.available_plenty')}</p>
				<p>○＝{$t('artist.schedule.available')}</p>
				<p>×＝{$t('artist.schedule.not')}</p>
				<p>ー＝{$t('artist.schedule.not_yet')}</p>
			</div>
		</section>
		<h2 class="inner-header font-base-jp">{$t('artist.achievements')}</h2>
		<section class="achievement-section">
			{#if data.artist?.jobs !== undefined}
				{#each data.artist?.jobs as job}
					<h3 class="font-base-jp font-bold">{job.name[lang]}</h3>
					<hr class="hr-achievement hr-standard" />
					<div class="achievement-list">
						{#each job.achievements as achievement}
							<div class="achievement-row">
								<p class="font-base-jp">{achievement.year}</p>
								<a href={achievement.link} target="_blank"><p class="link font-base-jp">{achievement.text}</p></a
								>
							</div>
						{/each}
					</div>
				{/each}
			{/if}
		</section>
		<section class="bottom-buttons">
			<Button link="../../request" size="large">{$t('artists.request')}</Button>
			<Button link=".." size="large" style="outline"
				>{$t('artists.back')}</Button
			>
		</section>
	</main>
</CommonPageLayout>

<style lang="scss">
	@use '$lib/assets/css/mq-settings' as mq;
	@use '$lib/assets/css/variables' as var;

	h3 {
		// font-family: var.$font-base;
		color: var.$color-font-base;
		margin: 20px 0;
		font-size: 20px;
	}
	main {
		width: 100%;
		padding: 60px 50px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	.top-section {
		width: 100%;
		display: grid;
		grid-template-columns: 1fr 1fr;
		margin: 10px auto 50px;
		gap: 30px;
		@include mq.mq($until: tablet) {
			place-items: center;
			grid-template-columns: 1fr;
		}
	}
	.carousel-wrapper {
		position: relative;
		min-width: 0; // これがないとwidthが0になる
		@include mq.mq($until: tablet) {
			max-width: 400px;
		}
	}
	.carousel-main-wrapper {
		position: absolute;
		top: 0;
	}
	.description-wrapper {
		padding: 0 30px;
		margin-bottom: 32px;
		p {
			// font-family: var.$font-base;
			color: var.$color-font-base;
			font-size: 14px;
		}
		@include mq.mq($until: tablet) {
			padding: 0px;
		}
	}
	.inner-header {
		// font-family: var.$font-base;
		font-size: 24px;
		font-weight: 700;
		margin: 30px 0;
		color: var.$color-font-base;
	}
	.calendar-wrapper {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 20px;
		margin-bottom: 20px;
		@include mq.mq($until: tablet) {
			grid-template-columns: 1fr;
		}
	}
	.calendar-per-year {
		display: flex;
		flex-direction: column;
		align-items: center;
		p {
			margin-bottom: 10px;
		}
	}
	table {
		background-color: var.$color-border-grey;
	}
	td {
		width: 29px;
		height: 29px;
		text-align: center;
		// font-family: var.$font-base;
		font-weight: 400;
	}
	.month {
		background-color: var.$color-accent-yellow;
	}
	.status {
		background-color: white;
		&.status-bad {
			background-color: var.$color-light-bg-grey;
		}
	}
	.status-description {
		display: flex;
		align-items: center;
		width: 100%;
		gap: 10px;
		justify-content: center;
		padding: 10px 0;
		@include mq.mq($until: tablet) {
			flex-direction: column;
		}
	}
	.achievement-section {
		width: 100%;
		margin: 0 0 10px;
	}
	.hr-achievement {
		height: 2.5px;
		width: 100%;
	}
	.achievement-list {
		display: grid;
		grid-template-columns: 1fr 4fr;
		margin-bottom: 20px;
		a {
			text-decoration: none;
		}
		p {
			padding: 8px;
			color: var.$color-font-base;
		}
		.link {
			text-decoration: solid underline var.$color-font-base;
		}
	}
	.achievement-row {
		width: 100%;
		display: contents;
		> * {
			border-bottom: 1px solid var.$color-border-grey;
		}
	}
	.bottom-buttons {
		display: flex;
		flex-direction: column;
		gap: 20px;
		margin: 30px auto;
	}
	.mobile {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		@include mq.mq($from: tablet) {
			display: none;
		}
	}
	.desktop {
		@include mq.mq($until: tablet) {
			display: none;
		}
	}
</style>
